import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Button, CircularProgress, TextField, MenuItem, Dialog, DialogTitle, Checkbox, FormControlLabel, Collapse } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import { getServerUri } from "../../modules/uriUtility";

const ENVS = ["development", "test", "production"];

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    minHeight: "100vh",
    marginLeft: "21.5rem",
    backgroundColor: "#404040",
    color: "white",
    overflowY: "auto",
    padding: "2rem"
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "1.5rem"
  },
  title: {
    fontSize: 30,
    fontWeight: 540,
    marginBottom: "1rem"
  },
  homeEnv: {
    marginTop: "1rem",
    fontSize: "0.95rem",
    color: "#9aa0a0"
  },
  topFields: {
    display: "flex",
    gap: "2rem",
    maxWidth: 1100,
    margin: "0 auto 2rem auto"
  },
  topField: {
    flex: 1
  },
  columns: {
    display: "flex",
    gap: "1.5rem",
    alignItems: "stretch"
  },
  column: {
    flex: 1,
    backgroundColor: "#4b4b4b"
  },
  columnInner: {
    padding: "1.5rem",
    display: "flex",
    flexDirection: "column",
    height: "100%"
  },
  columnTitle: {
    fontSize: 20,
    fontWeight: 540,
    marginBottom: "0.25rem"
  },
  columnHint: {
    fontSize: "0.85rem",
    color: "#9aa0a0",
    marginBottom: "1rem",
    height: "3.6rem",
    overflow: "hidden"
  },
  fieldsArea: {
    flex: "0 0 auto",
    width: "100%",
    minWidth: 0
  },
  inputRow: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    alignItems: "stretch"
  },
  previewBox: {
    flex: "0 0 auto",
    width: "100%",
    minWidth: 0,
    height: "16rem",
    overflow: "auto",
    backgroundColor: "#3a3a3a",
    border: "1px solid #555",
    borderRadius: 4,
    padding: "0.5rem 0.75rem"
  },
  previewLabel: {
    fontSize: "0.75rem",
    color: "#9aa0a0",
    marginBottom: "0.4rem",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    whiteSpace: "nowrap"
  },
  previewPre: {
    margin: 0,
    fontFamily: "monospace",
    fontSize: "0.78rem",
    color: "#cfcfcf",
    whiteSpace: "pre"
  },
  overwriteCheck: {
    marginTop: "auto",
    paddingTop: "0.75rem"
  },
  applyRow: {
    display: "flex",
    justifyContent: "center",
    gap: "1rem",
    marginTop: "2rem"
  },
  status: {
    marginTop: "1.5rem",
    textAlign: "center",
    fontSize: "1.2rem"
  },
  statusError: {
    color: "#ff7a7a"
  },
  statusOk: {
    color: "#7aff7a"
  },
  historyCard: {
    width: "100%",
    marginTop: "2.5rem",
    backgroundColor: "#383838",
    borderRadius: 8,
    overflow: "hidden"
  },
  historyTitleBar: {
    padding: "1rem 1.5rem",
    backgroundColor: "#2f2f2f",
    color: "#ffffff",
    fontSize: 20,
    fontWeight: 600
  },
  historyBody: {
    padding: "1.25rem 1.5rem"
  },
  baseHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    padding: "0.6rem 1rem",
    borderRadius: 5,
    margin: "0.5rem 0 0.5rem 1rem",
    cursor: "pointer",
    userSelect: "none",
    fontWeight: "bold",
    backgroundColor: "#444444",
    color: "#e6e6e6"
  },
  baseBody: {
    paddingLeft: "1.5rem"
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    padding: "0.75rem 1.1rem",
    borderRadius: 6,
    marginBottom: "0.5rem",
    cursor: "pointer",
    userSelect: "none",
    fontWeight: "bold",
    backgroundColor: "#4b4b4b",
    color: "#ffffff"
  },
  groupHeaderPending: {
    backgroundColor: "#5a4a1d",
    color: "#ffcf6b"
  },
  groupBody: {
    paddingLeft: "0.5rem",
    marginBottom: "1rem"
  },
  entryCard: {
    backgroundColor: "#4b4b4b",
    borderRadius: 6,
    padding: "1rem 1.25rem",
    marginBottom: "0.75rem"
  },
  entryHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "1rem"
  },
  entryFeatures: {
    color: "#9aa0a0",
    fontSize: "0.8rem",
    marginTop: "0.25rem",
    wordBreak: "break-all"
  },
  pendingBadge: {
    display: "inline-block",
    marginTop: "0.6rem",
    padding: "0.25rem 0.6rem",
    borderRadius: 4,
    backgroundColor: "#5a4a1d",
    color: "#ffcf6b",
    fontSize: "0.8rem",
    fontWeight: "bold"
  },
  envRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginTop: "0.6rem",
    flexWrap: "wrap"
  },
  envChip: {
    minWidth: 120,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.5
  }
}));

const FIELDS_PLACEHOLDER = '"fontSize": 0.5, "caption": "Counter"';
const VERSION_FIELDS_PLACEHOLDER =
  '"position": {"portrait": {"x": 0.5, "y": 0.5}, "landscape": {"x": 0.5, "y": 0.5}}, "scale": {"portrait": 0.5, "landscape": 0.5}';

const envColor = (env) => (env === "production" ? "#f44336" : env === "test" ? "#d9a02b" : "#5bb3b3");

export default function UpdateDatabase(props) {
  const classes = useStyles();
  const accessToken = localStorage.getItem("session.accessToken");
  const selectedBase = props.selectedBase;
  const baseIDFromUrl = new URLSearchParams(window.location.search).get("baseID") || "";
  const currentBaseTitle = (selectedBase && selectedBase.title) || "";

  const [busy, setBusy] = useState(false);
  const [homeEnv, setHomeEnv] = useState("");
  const [entries, setEntries] = useState([]);
  const [baseTitles, setBaseTitles] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});
  const [expandedBases, setExpandedBases] = useState({});
  const [componentIds, setComponentIds] = useState([]);
  const [addingNew, setAddingNew] = useState(false);
  const [versionPreview, setVersionPreview] = useState("");
  const [localizationPreview, setLocalizationPreview] = useState("");
  const [previewLang, setPreviewLang] = useState("");
  const baseID = baseIDFromUrl || (selectedBase && selectedBase._id ? String(selectedBase._id) : "");
  const [componentID, setComponentID] = useState("");
  const [localizationFields, setLocalizationFields] = useState("");
  const [versionFields, setVersionFields] = useState("");
  const [localizationOverwrite, setLocalizationOverwrite] = useState(false);
  const [versionOverwrite, setVersionOverwrite] = useState(false);
  const [status, setStatus] = useState({ kind: null, message: "" });
  const [applyConfirmDialog, setApplyConfirmDialog] = useState({ open: false, context: null });
  const [conflictDialog, setConflictDialog] = useState({ open: false, context: null, conflictCount: 0 });
  const [removeConfirmDialog, setRemoveConfirmDialog] = useState({ open: false, entry: null, env: null });
  const [assetWarningDialog, setAssetWarningDialog] = useState({ open: false, counts: null });
  const [emptyFill, setEmptyFill] = useState({ loading: false, checked: false, report: null });
  const [emptyFillDialog, setEmptyFillDialog] = useState({ open: false, report: null });
  const [assetStatus, setAssetStatus] = useState({ loading: false, totalVersions: 0, components: [] });
  const [assetComponent, setAssetComponent] = useState("");
  const [assetVersion, setAssetVersion] = useState("");
  const [blankFillOpen, setBlankFillOpen] = useState(false);
  const [propagationOpen, setPropagationOpen] = useState(false);
  const [assetChecked, setAssetChecked] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const mode = !addingNew && componentIds.includes(componentID.trim()) ? "update" : "new";

  const newComponentIDs = Array.from(
    new Set(
      entries
        .filter((entry) => String(entry.baseID) === String(baseID))
        .map((entry) => entry.componentID)
    )
  ).filter(Boolean);

  const historyRef = useRef(null);

  const pendingEnvOf = (entry) => ["test", "production"].some((env) => !(entry.envs && entry.envs[env]));
  const pendingCount = entries.filter(pendingEnvOf).length;

  const openHistoryToPending = () => {
    const userDefaults = {};
    const baseDefaults = {};
    entries.forEach((entry) => {
      if (!pendingEnvOf(entry)) return;
      const user = entry.createdByName || "unknown";
      userDefaults[user] = true;
      baseDefaults[user + "|" + (entry.baseID || "unknown")] = true;
    });
    setExpandedUsers((prev) => ({ ...prev, ...userDefaults }));
    setExpandedBases((prev) => ({ ...prev, ...baseDefaults }));
    setHistoryOpen(true);
    setTimeout(() => {
      if (historyRef.current) historyRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const parseFields = (raw) => {
    const trimmed = raw.trim();
    if (trimmed === "") {
      return null;
    }
    const normalized = trimmed
      .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
      .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
      .replace(/[\u00A0\u2007\u202F]/g, " ");
    const sanitized = ("{" + normalized + "}").replace(/,(\s*[}\]])/g, "$1");
    return JSON.stringify(JSON.parse(sanitized));
  };

  const buildOperations = () => {
    const operations = [];
    const localizationFeatures = parseFields(localizationFields);
    if (localizationFeatures)
      operations.push({ settingsType: "localizationSettings", features: localizationFeatures, overwrite: localizationOverwrite });
    const versionFeatures = parseFields(versionFields);
    if (versionFeatures)
      operations.push({ settingsType: "versionSettings", features: versionFeatures, overwrite: versionOverwrite });
    return operations;
  };

  const sendUpdateDatabase = async (payload) => {
    const formBody = new URLSearchParams();
    formBody.append("accessToken", accessToken);
    formBody.append("baseID", payload.baseID);
    formBody.append("mode", payload.mode);
    formBody.append("componentID", payload.componentID);
    formBody.append("settingsType", payload.settingsType || "versionSettings");
    formBody.append("features", payload.features || "{}");
    formBody.append("overwrite", payload.overwrite ? "true" : "false");
    formBody.append("dryRun", payload.dryRun ? "true" : "false");
    formBody.append("remove", payload.remove ? "true" : "false");
    formBody.append("removeComponent", payload.removeComponent ? "true" : "false");
    formBody.append("targetEnv", payload.targetEnv || "");
    const response = await fetch(getServerUri("versionTransfer/updateDatabase"), {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: formBody.toString()
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || "UPDATE_DB_FAILED");
    }
    return response.json();
  };

  const loadEntries = async () => {
    try {
      const formBody = new URLSearchParams();
      formBody.append("accessToken", accessToken);
      formBody.append("baseID", baseID.trim());
      const response = await fetch(getServerUri("versionTransfer/updateDatabaseEntryList"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: formBody.toString()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.env) setHomeEnv(data.env);
        setBaseTitles(data.baseTitles || {});
        const loadedEntries = data.entries || [];
        setEntries(loadedEntries);
        const userDefaults = {};
        const baseDefaults = {};
        loadedEntries.forEach((entry) => {
          const user = entry.createdByName || "unknown";
          const hasPending = ENVS.some((env) => !(entry.envs && entry.envs[env]));
          if (hasPending) {
            userDefaults[user] = true;
            baseDefaults[user + "|" + (entry.baseID || "unknown")] = true;
          }
        });
        setExpandedUsers(userDefaults);
        setExpandedBases(baseDefaults);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const toggleUser = (user) => setExpandedUsers((prev) => ({ ...prev, [user]: !prev[user] }));
  const toggleBase = (key) => setExpandedBases((prev) => ({ ...prev, [key]: !prev[key] }));

  const hasPending = (list) =>
    list.some((entry) => ENVS.some((env) => !(entry.envs && entry.envs[env])));

  const groupEntriesByUserAndBase = () => {
    const users = [];
    const userIndex = {};
    entries.forEach((entry) => {
      const user = entry.createdByName || "unknown";
      if (userIndex[user] === undefined) {
        userIndex[user] = users.length;
        users.push({ user, allEntries: [], bases: [], baseIndex: {} });
      }
      const userGroup = users[userIndex[user]];
      userGroup.allEntries.push(entry);
      const base = entry.baseID || "unknown";
      if (userGroup.baseIndex[base] === undefined) {
        userGroup.baseIndex[base] = userGroup.bases.length;
        userGroup.bases.push({ baseID: base, entries: [] });
      }
      userGroup.bases[userGroup.baseIndex[base]].entries.push(entry);
    });
    const entryPending = (entry) => ENVS.some((env) => !(entry.envs && entry.envs[env]));
    users.forEach((userGroup) => {
      userGroup.bases.forEach((baseGroup) => {
        baseGroup.entries.sort((a, b) => (entryPending(a) ? 0 : 1) - (entryPending(b) ? 0 : 1));
      });
      userGroup.bases.sort((a, b) => (hasPending(a.entries) ? 0 : 1) - (hasPending(b.entries) ? 0 : 1));
    });
    users.sort((a, b) => (hasPending(a.allEntries) ? 0 : 1) - (hasPending(b.allEntries) ? 0 : 1));
    return users;
  };

  const loadComponentIds = async () => {
    if (!baseID.trim()) return;
    try {
      const formBody = new URLSearchParams();
      formBody.append("accessToken", accessToken);
      formBody.append("baseID", baseID.trim());
      const response = await fetch(getServerUri("versionTransfer/componentIdList"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: formBody.toString()
      });
      if (response.ok) {
        const data = await response.json();
        setComponentIds(data.componentIds || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPreview = async (id) => {
    if (!baseID.trim() || !id) {
      setVersionPreview("");
      setLocalizationPreview("");
      setPreviewLang("");
      return;
    }
    try {
      const formBody = new URLSearchParams();
      formBody.append("accessToken", accessToken);
      formBody.append("baseID", baseID.trim());
      formBody.append("componentID", id);
      const response = await fetch(getServerUri("versionTransfer/componentPreview"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: formBody.toString()
      });
      if (response.ok) {
        const data = await response.json();
        setVersionPreview(data.versionSettings ? JSON.stringify(data.versionSettings, null, 2) : "");
        setLocalizationPreview(data.localization ? JSON.stringify(data.localization, null, 2) : "");
        setPreviewLang(data.language || "");
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadEntries();
    loadComponentIds();
  }, []);

  useEffect(() => {
    const candidates = assetStatus.components.filter((c) => c.missingCount > 0 && newComponentIDs.includes(c.componentID));
    if (candidates.length === 0) {
      if (assetComponent) setAssetComponent("");
      return;
    }
    if (!candidates.some((c) => c.componentID === assetComponent)) {
      setAssetComponent(candidates[0].componentID);
    }
  }, [assetStatus]);

  useEffect(() => {
    const comp = assetStatus.components.find((c) => c.componentID === assetComponent);
    if (!comp) {
      if (assetVersion) setAssetVersion("");
      return;
    }
    if (!comp.sources.some((s) => s.versionID === assetVersion)) {
      setAssetVersion(comp.sources[0] ? comp.sources[0].versionID : "");
    }
  }, [assetComponent, assetStatus]);

  useEffect(() => {
    if (componentIds.includes(componentID.trim())) {
      fetchPreview(componentID.trim());
    } else {
      setVersionPreview("");
      setLocalizationPreview("");
      setPreviewLang("");
    }
  }, [componentID]);

  const sendOperation = (context, operation, opts) =>
    sendUpdateDatabase({
      baseID: context.baseID,
      mode: context.mode,
      componentID: context.componentID,
      settingsType: operation.settingsType,
      features: operation.features,
      overwrite: opts.overwrite,
      dryRun: opts.dryRun,
      targetEnv: context.env
    });

  const applyOperations = async (context) => {
    setBusy(true);
    try {
      const updatedVersions = new Set();
      const languages = new Set();
      let missing = 0;
      for (const operation of context.operations) {
        const report = await sendOperation(context, operation, { overwrite: operation.overwrite, dryRun: false });
        report.affectedVersionIDs.forEach((versionID) => updatedVersions.add(versionID));
        report.languagesTouched.forEach((language) => languages.add(language));
        missing = report.missingComponentVersions.length;
      }
      const parts = [`Updated ${updatedVersions.size} version(s)`];
      if (languages.size > 0) parts.push(`languages: ${Array.from(languages).join(", ")}`);
      if (missing > 0) parts.push(`${missing} version(s) had no "${context.componentID}" component (skipped)`);
      setStatus({ kind: "ok", message: `[${context.env}] ` + parts.join(". ") + "." });
      loadEntries();
      loadComponentIds();
      fetchPreview(componentID.trim());
      if (context.mode === "new") {
        await promptAssetWarning();
      }
    } catch (err) {
      setStatus({ kind: "error", message: "Apply failed: " + err.message });
    }
    setBusy(false);
  };

  const promptAssetWarning = async () => {
    try {
      const formBody = new URLSearchParams();
      formBody.append("accessToken", accessToken);
      formBody.append("baseID", baseID.trim());
      formBody.append("componentID", componentID.trim());
      const response = await fetch(getServerUri("versionTransfer/componentAssetRequirements"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: formBody.toString()
      });
      if (!response.ok) return;
      const data = await response.json();
      const counts = data.counts || { image: 0, atlas: 0, sound: 0 };
      const total = (counts.image || 0) + (counts.atlas || 0) + (counts.sound || 0);
      if (data.found && total > 0) {
        setAssetWarningDialog({ open: true, counts });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadAssetStatus = async () => {
    if (!baseID.trim()) return;
    setAssetChecked(true);
    setAssetStatus((prev) => ({ ...prev, loading: true }));
    try {
      const formBody = new URLSearchParams();
      formBody.append("accessToken", accessToken);
      formBody.append("baseID", baseID.trim());
      const response = await fetch(getServerUri("versionTransfer/componentAssetStatus"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: formBody.toString()
      });
      if (!response.ok) {
        setAssetStatus({ loading: false, totalVersions: 0, components: [] });
        return;
      }
      const data = await response.json();
      setAssetStatus({
        loading: false,
        totalVersions: data.totalVersions || 0,
        components: data.components || []
      });
    } catch (err) {
      setAssetStatus({ loading: false, totalVersions: 0, components: [] });
    }
  };

  const applyPropagation = async () => {
    if (!assetComponent || !assetVersion) {
      setStatus({ kind: "error", message: "Pick a component and a source version first." });
      return;
    }
    setBusy(true);
    setStatus({ kind: null, message: "" });
    try {
      const formBody = new URLSearchParams();
      formBody.append("accessToken", accessToken);
      formBody.append("baseID", baseID.trim());
      formBody.append("componentID", assetComponent);
      formBody.append("sourceVersionID", assetVersion);
      formBody.append("dryRun", "false");
      const response = await fetch(getServerUri("versionTransfer/copyComponentAssets"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: formBody.toString()
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || response.status);
      }
      const data = await response.json();
      const skippedPart = data.skippedCount > 0 ? ` ${data.skippedCount} already had assets (skipped).` : "";
      setStatus({
        kind: "ok",
        message: `Copied "${assetComponent}" assets (${data.keys.join(", ")}) to ${data.copiedCount} version(s).` + skippedPart
      });
      loadAssetStatus();
    } catch (err) {
      setStatus({ kind: "error", message: "Asset apply failed: " + err.message });
    }
    setBusy(false);
  };

  const sendFillEmptyAssets = async (dryRun) => {
    const formBody = new URLSearchParams();
    formBody.append("accessToken", accessToken);
    formBody.append("componentIDs", JSON.stringify(newComponentIDs));
    formBody.append("baseID", baseID.trim());
    formBody.append("dryRun", dryRun ? "true" : "false");
    const response = await fetch(getServerUri("versionTransfer/fillEmptyAssets"), {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: formBody.toString()
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || response.status);
    }
    return response.json();
  };

  const checkEmptyAssets = async () => {
    if (!baseID.trim()) return;
    setBusy(true);
    setStatus({ kind: null, message: "" });
    setEmptyFill({ loading: true, checked: false, report: null });
    try {
      const report = await sendFillEmptyAssets(true);
      setEmptyFill({ loading: false, checked: true, report });
      if (report.slotWrites === 0) {
        setStatus({ kind: "ok", message: "All component asset slots are already filled. Nothing to do." });
      } else {
        setStatus({
          kind: "ok",
          message: `${report.versionsAffected} version(s) have ${report.slotWrites} empty asset slot(s) across ${report.componentsNeedingAssets} component(s).`
        });
      }
    } catch (err) {
      setEmptyFill({ loading: false, checked: false, report: null });
      setStatus({ kind: "error", message: "Asset check failed: " + err.message });
    }
    setBusy(false);
  };

  const applyEmptyAssets = async () => {
    setEmptyFillDialog({ open: false, report: null });
    setBusy(true);
    setStatus({ kind: null, message: "" });
    try {
      const report = await sendFillEmptyAssets(false);
      setStatus({
        kind: "ok",
        message: `Filled ${report.slotWrites} empty slot(s) in ${report.versionsAffected} version(s) with blank placeholders.`
      });
      setEmptyFill({ loading: false, checked: false, report: null });
    } catch (err) {
      setStatus({ kind: "error", message: "Blank asset apply failed: " + err.message });
    }
    setBusy(false);
  };

  const runApply = async (context) => {
    setBusy(true);
    setStatus({ kind: null, message: "" });
    try {
      let totalVersions = 0;
      let missingInAll = true;
      let overwriteConflictCount = 0;
      for (const operation of context.operations) {
        const report = await sendOperation(context, operation, { overwrite: false, dryRun: true });
        totalVersions = report.totalVersions;
        if (report.missingComponentVersions.length !== report.totalVersions) missingInAll = false;
        if (operation.overwrite) overwriteConflictCount += report.conflicts.length;
      }
      if (totalVersions === 0) {
        setStatus({ kind: "error", message: `[${context.env}] No versions were found for this base.` });
        setBusy(false);
        return;
      }
      if (context.mode === "update" && missingInAll) {
        setStatus({
          kind: "error",
          message: `[${context.env}] No version has a component with ID "${context.componentID}". Nothing to update.`
        });
        setBusy(false);
        return;
      }
      if (overwriteConflictCount > 0) {
        setBusy(false);
        setConflictDialog({ open: true, context, conflictCount: overwriteConflictCount });
        return;
      }
      setBusy(false);
      await applyOperations(context);
    } catch (err) {
      setStatus({ kind: "error", message: `[${context.env}] Check failed: ` + err.message });
      setBusy(false);
    }
  };

  const checkComponentInBuild = async () => {
    const formBody = new URLSearchParams();
    formBody.append("accessToken", accessToken);
    formBody.append("baseID", baseID.trim());
    formBody.append("componentID", componentID.trim());
    const response = await fetch(getServerUri("versionTransfer/componentExists"), {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: formBody.toString()
    });
    if (!response.ok) return { verified: false, exists: false };
    return response.json();
  };

  const handleApplyToEnv = async (env) => {
    if (!baseID.trim()) {
      setStatus({ kind: "error", message: "Base ID is required." });
      return;
    }
    if (!componentID.trim()) {
      setStatus({ kind: "error", message: "Component ID is required." });
      return;
    }
    let operations;
    try {
      operations = buildOperations();
    } catch (e) {
      setStatus({ kind: "error", message: "Fields must be valid (e.g. " + FIELDS_PLACEHOLDER + ")." });
      return;
    }
    if (operations.length === 0) {
      setStatus({ kind: "error", message: "Enter at least one field in Localization or Version settings." });
      return;
    }
    setStatus({ kind: null, message: "" });
    if (mode === "new") {
      setBusy(true);
      try {
        const check = await checkComponentInBuild();
        if (check.verified && !check.exists) {
          setStatus({
            kind: "error",
            message: (
              <span>
                <b>{componentID.trim()}</b> was not found in the game. Cannot add a component the game does not
                support.
              </span>
            )
          });
          setBusy(false);
          return;
        }
      } catch (err) {
        console.error(err);
      }
      setBusy(false);
    }
    setApplyConfirmDialog({
      open: true,
      context: { baseID: baseID.trim(), mode, componentID: componentID.trim(), operations, env }
    });
  };

  const handleReapplyEntry = (entry, env) => {
    setStatus({ kind: null, message: "" });
    setApplyConfirmDialog({
      open: true,
      context: {
        baseID: entry.baseID,
        mode: entry.mode,
        componentID: entry.componentID,
        operations: [{ settingsType: entry.settingsType, features: entry.features, overwrite: entry.overwrite === true }],
        env
      }
    });
  };

  const removeEntryFromEnv = async (entry, env) => {
    setBusy(true);
    setStatus({ kind: null, message: "" });
    try {
      const report = await sendUpdateDatabase({
        baseID: entry.baseID,
        mode: entry.mode,
        componentID: entry.componentID,
        settingsType: entry.settingsType,
        features: entry.features,
        remove: true,
        targetEnv: env
      });
      setStatus({
        kind: "ok",
        message: `[${env}] Removed "${entry.componentID}" fields from ${report.removedCount} version(s).`
      });
      loadEntries();
      loadComponentIds();
      fetchPreview(componentID.trim());
    } catch (err) {
      setStatus({ kind: "error", message: `[${env}] Remove failed: ` + err.message });
    }
    setBusy(false);
  };

  const renderEnvBanner = (env) => (
    <Typography
      style={{
        fontSize: 30,
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: 1,
        color: envColor(env),
        textAlign: "center",
        margin: "0.6rem 0"
      }}
    >
      {env}
    </Typography>
  );

  const renderPreview = (content, label) => (
    <div className={classes.previewBox}>
      <div className={classes.previewLabel}>{label}</div>
      <pre className={classes.previewPre}>{content || "— no current value in DB —"}</pre>
    </div>
  );

  const renderEntryCard = (entry) => {
    const appliedEnvs = ENVS.filter((env) => entry.envs && entry.envs[env]);
    const pendingEnvs = ENVS.filter((env) => !(entry.envs && entry.envs[env]));
    return (
      <div key={entry._id} className={classes.entryCard}>
        <div className={classes.entryHead}>
          <span style={{ fontWeight: "bold", color: "#ffffff" }}>
            {(entry.mode === "new" ? "add component" : "add fields") +
              " · " +
              entry.componentID +
              " · " +
              entry.settingsType}
          </span>
        </div>
        {entry.features && <div className={classes.entryFeatures}>{entry.features}</div>}

        {appliedEnvs.length > 0 && pendingEnvs.length > 0 && (
          <div className={classes.pendingBadge}>
            {"⚠ Not yet applied to: " + pendingEnvs.map((env) => env.toUpperCase()).join(", ")}
          </div>
        )}

        {ENVS.map((env) => {
          const info = entry.envs && entry.envs[env];
          return (
            <div key={env} className={classes.envRow}>
              <span className={classes.envChip} style={{ color: envColor(env) }}>
                {env}
              </span>
              {info ? (
                <>
                  <span style={{ color: "#7aff7a", fontSize: "0.85rem" }}>
                    {"✓ applied" +
                      (info.userName ? " by " + info.userName : "") +
                      (info.appliedAt ? " · " + new Date(info.appliedAt).toLocaleString() : "")}
                  </span>
                  <Button
                    size="small"
                    variant="outlined"
                    style={{ color: "#ff9a9a", borderColor: "#a85a5a", fontWeight: "bold" }}
                    onClick={() => setRemoveConfirmDialog({ open: true, entry, env })}
                    disabled={busy}
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  style={{ backgroundColor: envColor(env), color: "#ffffff", fontWeight: "bold" }}
                  onClick={() => handleReapplyEntry(entry, env)}
                  disabled={busy}
                >
                  {"Apply to " + env.toUpperCase()}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={classes.root}>
      {pendingCount > 0 && (
        <div
          title={`${pendingCount} update(s) not yet applied to test/production — click to go to History`}
          onClick={openHistoryToPending}
          style={{
            position: "absolute",
            top: "1rem",
            right: "1.5rem",
            cursor: "pointer",
            fontSize: "2rem",
            lineHeight: 1,
            userSelect: "none"
          }}
        >
          ⚠️
        </div>
      )}
      <div className={classes.header}>
        <Typography className={classes.title}>Version Update</Typography>
        {homeEnv !== "" && (
          <Typography className={classes.homeEnv}>
            Apply writes to the{" "}
            <b style={{ color: envColor(homeEnv), textTransform: "uppercase" }}>{homeEnv}</b> database (this dashboard).
            Propagate to other databases from History below.
          </Typography>
        )}
      </div>

      <div className={classes.topFields}>
        <div className={classes.topField}>
          <Typography style={{ fontSize: "1.1rem", color: "#9aa0a0" }}>Base</Typography>
          <Typography style={{ fontSize: "1.4rem", fontWeight: 600 }}>{currentBaseTitle || "—"}</Typography>
          <Typography style={{ fontSize: "0.85rem", color: "#9aa0a0", wordBreak: "break-all" }}>{baseID}</Typography>
        </div>
        <div className={classes.topField}>
          {addingNew ? (
            <TextField
              fullWidth
              label="New Component ID"
              value={componentID}
              onChange={(e) => setComponentID(e.target.value)}
              helperText="Will be added as a NEW component"
              InputProps={{ style: { fontSize: "1.4rem", padding: "0.4rem 0" } }}
              InputLabelProps={{ style: { fontSize: "1.1rem" } }}
            />
          ) : (
            <TextField
              select
              fullWidth
              label="Component ID"
              value={componentID}
              onChange={(e) => setComponentID(e.target.value)}
              InputProps={{ style: { fontSize: "1.4rem", padding: "0.4rem 0" } }}
              InputLabelProps={{ style: { fontSize: "1.1rem" } }}
            >
              {componentIds.length === 0 && (
                <MenuItem value="" disabled>
                  No existing components
                </MenuItem>
              )}
              {componentIds.map((id) => (
                <MenuItem key={id} value={id}>
                  {id}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button
            size="small"
            onClick={() => {
              setComponentID("");
              setAddingNew(!addingNew);
            }}
            style={{ color: "#9bcec0", fontWeight: "bold", marginTop: "0.4rem" }}
          >
            {addingNew ? "← Choose existing component" : "+ Add new component ID"}
          </Button>
        </div>
      </div>


      <>
          <div className={classes.columns}>
            <Card className={classes.column}>
              <div className={classes.columnInner}>
                <Typography className={classes.columnTitle}>Localization Settings</Typography>
                <Typography className={classes.columnHint}>
                  Written to every active (created) language of each version. Existing values are never overwritten
                  unless you confirm.
                </Typography>
                <div className={classes.inputRow}>
                  <TextField
                    className={classes.fieldsArea}
                    multiline
                    minRows={10}
                    maxRows={10}
                    placeholder={FIELDS_PLACEHOLDER}
                    value={localizationFields}
                    onChange={(e) => setLocalizationFields(e.target.value)}
                    inputProps={{ wrap: "off", style: { whiteSpace: "pre", overflow: "auto" } }}
                  />
                  {renderPreview(localizationPreview, "Current in DB" + (previewLang ? " (" + previewLang + ")" : ""))}
                </div>
                <FormControlLabel
                  className={classes.overwriteCheck}
                  control={
                    <Checkbox
                      checked={localizationOverwrite}
                      onChange={(e) => setLocalizationOverwrite(e.target.checked)}
                      style={{ color: "#418989" }}
                    />
                  }
                  label="Update existing values (overwrite)"
                />
              </div>
            </Card>
            <Card className={classes.column}>
              <div className={classes.columnInner}>
                <Typography className={classes.columnTitle}>Version Settings</Typography>
                <Typography className={classes.columnHint}>
                  Written once per version directly on the component (not per language).
                </Typography>
                <div className={classes.inputRow}>
                  <TextField
                    className={classes.fieldsArea}
                    multiline
                    minRows={10}
                    maxRows={10}
                    placeholder={VERSION_FIELDS_PLACEHOLDER}
                    value={versionFields}
                    onChange={(e) => setVersionFields(e.target.value)}
                    inputProps={{ wrap: "off", style: { whiteSpace: "pre", overflow: "auto" } }}
                  />
                  {renderPreview(versionPreview, "Current in DB")}
                </div>
                <FormControlLabel
                  className={classes.overwriteCheck}
                  control={
                    <Checkbox
                      checked={versionOverwrite}
                      onChange={(e) => setVersionOverwrite(e.target.checked)}
                      style={{ color: "#418989" }}
                    />
                  }
                  label="Update existing values (overwrite)"
                />
              </div>
            </Card>
          </div>

          <div className={classes.applyRow}>
            <Button
              variant="contained"
              style={{ backgroundColor: "#418989", color: "#ffffff", fontWeight: "bold" }}
              onClick={() => handleApplyToEnv(homeEnv)}
              disabled={busy || homeEnv === ""}
            >
              {busy ? <CircularProgress size={20} /> : "Apply"}
            </Button>
          </div>
      </>

      {status.kind && (
        <div className={classes.status + " " + (status.kind === "error" ? classes.statusError : classes.statusOk)}>
          {status.message}
        </div>
      )}

      <Card style={{ margin: "2rem 0 1rem", backgroundColor: "#2f3a40", border: "1px solid #6b8fb3" }}>
        <div
          style={{
            padding: "0.85rem 1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer"
          }}
          onClick={() => setBlankFillOpen((v) => !v)}
        >
          <Typography style={{ fontSize: "1.1rem", fontWeight: 600, color: "#9bc0e0" }}>
            Blank Asset Filler{newComponentIDs.length > 0 ? ` (${newComponentIDs.length})` : ""}
          </Typography>
          <Typography style={{ fontSize: "1.2rem", color: "#9bc0e0" }}>{blankFillOpen ? "▾" : "▸"}</Typography>
        </div>
        <Collapse in={blankFillOpen}>
          <div style={{ padding: "0 1rem 1rem" }}>
            <Typography style={{ fontSize: "0.85rem", color: "#bcccd6" }}>
              Fills empty asset slots of the components from the update History with blank placeholders
              (image → empty.png, atlas → empty.png + json, sound → empty.mp3) and marks them selected. Other
              components and real assets are never touched.
            </Typography>
            {newComponentIDs.length === 0 ? (
              <Typography style={{ fontSize: "0.85rem", color: "#9aa0a0", marginTop: "10px" }}>
                No components in the update History for this game yet.
              </Typography>
            ) : (
              <>
                <Typography
                  style={{ fontFamily: "monospace", fontSize: "0.8rem", color: "#9aa0a0", marginTop: "8px" }}
                >
                  {newComponentIDs.join(", ")}
                </Typography>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "12px", flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    disabled={busy || emptyFill.loading || !baseID.trim()}
                    style={{ borderColor: "#6b8fb3", color: "#9bc0e0", fontWeight: "bold" }}
                    onClick={checkEmptyAssets}
                  >
                    {emptyFill.loading ? "Checking…" : "Check version assets"}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={busy || !emptyFill.report || emptyFill.report.slotWrites === 0}
                    style={{
                      backgroundColor: emptyFill.report && emptyFill.report.slotWrites > 0 ? "#418989" : "#3a4a4a",
                      color: "#ffffff",
                      fontWeight: "bold"
                    }}
                    onClick={() => setEmptyFillDialog({ open: true, report: emptyFill.report })}
                  >
                    Apply blank assets to missing slots
                  </Button>
                </div>
                {emptyFill.checked && emptyFill.report && (
                  <div style={{ marginTop: "10px" }}>
                    <Typography
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: emptyFill.report.slotWrites > 0 ? "#ffcf6b" : "#9bcec0"
                      }}
                    >
                      {emptyFill.report.slotWrites > 0
                        ? `${emptyFill.report.componentsNeedingAssets} new component(s) need blank assets — ${emptyFill.report.slotWrites} empty slot(s) in ${emptyFill.report.versionsAffected} version(s):`
                        : "✓ The newly added component(s) already have their asset slots filled."}
                    </Typography>
                    {emptyFill.report.perComponent.map((c) => (
                      <Typography
                        key={c.componentID}
                        style={{ fontFamily: "monospace", fontSize: "0.85rem", color: "#d6e4f0", marginTop: "4px" }}
                      >
                        {`${c.componentID} : ${c.slotWrites} empty slot(s) in ${c.versionsMissing} version(s)`}
                      </Typography>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </Collapse>
      </Card>

      <Card style={{ margin: "0 0 1rem", backgroundColor: "#34403f", border: "1px solid #5bb3b3" }}>
        <div
          style={{
            padding: "0.85rem 1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer"
          }}
          onClick={() => setPropagationOpen((v) => !v)}
        >
          <Typography style={{ fontSize: "1.1rem", fontWeight: 600, color: "#9bcec0" }}>
            Asset Propagation ({assetStatus.components.filter((c) => c.missingCount > 0 && newComponentIDs.includes(c.componentID)).length})
          </Typography>
          <Typography style={{ fontSize: "1.2rem", color: "#9bcec0" }}>{propagationOpen ? "▾" : "▸"}</Typography>
        </div>
        <Collapse in={propagationOpen}>
          <div style={{ padding: "0.85rem 1rem 0" }}>
            <Button
              variant="outlined"
              disabled={busy || assetStatus.loading || !baseID.trim()}
              style={{ borderColor: "#5bb3b3", color: "#9bcec0", fontWeight: "bold" }}
              onClick={loadAssetStatus}
            >
              {assetStatus.loading ? "Checking…" : "Check versions"}
            </Button>
          </div>
          {!assetChecked ? (
            <div style={{ padding: "0.6rem 1rem 1rem" }}>
              <Typography style={{ fontSize: "0.85rem", color: "#9aa0a0" }}>
                Press "Check versions" to scan this game's versions for the newly added components.
              </Typography>
            </div>
          ) : (
            (() => {
            const candidates = assetStatus.components.filter((c) => c.missingCount > 0 && newComponentIDs.includes(c.componentID));
            if (candidates.length === 0) {
              return (
                <div style={{ padding: "0 1rem 1rem" }}>
                  <Typography style={{ fontSize: "0.85rem", color: "#9aa0a0" }}>
                    {assetStatus.totalVersions <= 1
                      ? "This game has a single version — there is nothing to propagate to."
                      : newComponentIDs.length === 0
                      ? "No components in the update History for this game yet."
                      : "Nothing to propagate — the newly added component(s) already have their assets in every version, or none has been uploaded to a version yet."}
                  </Typography>
                </div>
              );
            }
            const selectedComp = candidates.find((c) => c.componentID === assetComponent) || candidates[0];
            const selectedSource =
              selectedComp.sources.find((s) => s.versionID === assetVersion) || selectedComp.sources[0];
            return (
              <div style={{ padding: "0 1rem 1rem" }}>
                  <Typography style={{ fontSize: "0.85rem", color: "#bcccca" }}>
                    Pick a component and the version you uploaded its assets to. Only versions that don't already have
                    these assets are updated.
                  </Typography>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "12px", flexWrap: "wrap" }}>
                    <TextField
                      select
                      size="small"
                      label="Component"
                      value={selectedComp.componentID}
                      onChange={(e) => setAssetComponent(e.target.value)}
                      style={{ minWidth: 220 }}
                    >
                      {candidates.map((c) => (
                        <MenuItem key={c.componentID} value={c.componentID}>
                          {c.componentID}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Source version"
                      value={selectedSource ? selectedSource.versionID : ""}
                      onChange={(e) => setAssetVersion(e.target.value)}
                      style={{ minWidth: 240 }}
                    >
                      {selectedComp.sources.map((s) => (
                        <MenuItem key={s.versionID} value={s.versionID}>
                          {s.title || s.versionID}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      variant="contained"
                      disabled={busy || !selectedSource}
                      style={{ backgroundColor: "#418989", color: "#ffffff", fontWeight: "bold" }}
                      onClick={applyPropagation}
                    >
                      Apply to missing versions
                    </Button>
                  </div>
                  <Typography style={{ fontSize: "0.8rem", color: "#9aa0a0", marginTop: "8px" }}>
                    {`Missing in ${selectedComp.missingCount}/${assetStatus.totalVersions} version(s).`}
                  </Typography>
                  <div style={{ marginTop: "10px" }}>
                    <Typography style={{ fontSize: "0.85rem", fontWeight: 600, color: "#bcccca" }}>
                      Assets in this version:
                    </Typography>
                    {selectedSource && selectedSource.assets && selectedSource.assets.length > 0 ? (
                      selectedSource.assets.map((a) => (
                        <Typography
                          key={a.slot}
                          style={{ fontFamily: "monospace", fontSize: "0.85rem", color: "#d6e3e1" }}
                        >
                          {a.slot} : {a.files.join(", ")}
                        </Typography>
                      ))
                    ) : (
                      <Typography style={{ fontSize: "0.85rem", color: "#9aa0a0" }}>—</Typography>
                    )}
                  </div>
                </div>
              );
            })()
          )}
          </Collapse>
        </Card>

      <Card className={classes.historyCard} ref={historyRef}>
        <div
          className={classes.historyTitleBar}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
          onClick={() => setHistoryOpen((v) => !v)}
        >
          <span>Version Update History</span>
          <span>{historyOpen ? "▾" : "▸"}</span>
        </div>
        <Collapse in={historyOpen}>
        <div className={classes.historyBody}>
          {entries.length === 0 && <Typography style={{ color: "#9aa0a0" }}>No updates recorded yet.</Typography>}
          {groupEntriesByUserAndBase().map((userGroup) => {
            const userPending = hasPending(userGroup.allEntries);
            const userOpen = !!expandedUsers[userGroup.user];
            return (
              <div key={userGroup.user}>
                <div
                  className={classes.groupHeader + (userPending ? " " + classes.groupHeaderPending : "")}
                  onClick={() => toggleUser(userGroup.user)}
                >
                  <span>{(userPending ? "⚠ " : "") + userGroup.user + " (" + userGroup.allEntries.length + ")"}</span>
                  <span>{(userPending ? "needs attention · " : "") + (userOpen ? "▼" : "▶")}</span>
                </div>
                {userOpen && (
                  <div className={classes.groupBody}>
                    {userGroup.bases.map((baseGroup) => {
                      const basePending = hasPending(baseGroup.entries);
                      const baseKey = userGroup.user + "|" + baseGroup.baseID;
                      const baseOpen = !!expandedBases[baseKey];
                      const title = baseTitles[baseGroup.baseID] || "base";
                      return (
                        <div key={baseKey}>
                          <div
                            className={classes.baseHeader + (basePending ? " " + classes.groupHeaderPending : "")}
                            onClick={() => toggleBase(baseKey)}
                          >
                            <span>{(basePending ? "⚠ " : "") + title + " (" + baseGroup.baseID + ")"}</span>
                            <span>{(basePending ? "needs attention · " : "") + (baseOpen ? "▼" : "▶")}</span>
                          </div>
                          {baseOpen && (
                            <div className={classes.baseBody}>
                              {baseGroup.entries.map((entry) => renderEntryCard(entry))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </Collapse>
      </Card>

      <Card style={{ margin: "1rem 0 0", backgroundColor: "#3a3a3a", border: "1px solid #666" }}>
        <div
          style={{
            padding: "0.85rem 1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer"
          }}
          onClick={() => setHelpOpen((v) => !v)}
        >
          <Typography style={{ fontSize: "1.1rem", fontWeight: 600, color: "#cfcfcf" }}>
            How to use this page — guide
          </Typography>
          <Typography style={{ fontSize: "1.2rem", color: "#cfcfcf" }}>{helpOpen ? "▾" : "▸"}</Typography>
        </div>
        <Collapse in={helpOpen}>
          <div style={{ padding: "0 1.25rem 1.25rem", color: "#d6d6d6", fontSize: "0.9rem", lineHeight: 1.55 }}>
            {[
              {
                t: "What this page does",
                d: "When a game (base) gets a new component, or a new setting on an existing component, this page updates ALL of that base's existing versions in the DB without hand-writing mongo queries and without breaking existing data. It also manages missing assets (image/atlas/sound)."
              },
              {
                t: "1) Base",
                d: "Top of the page: the game you are working on. It is fixed (comes from the open game) and cannot be edited."
              },
              {
                t: "2) Component ID",
                d: "Pick an EXISTING component from the dropdown → mode 'update' (add fields): only versions that already have the component are touched; versions without it are skipped. Use '+ Add new component ID' to type an ID → mode 'new' (add component): the component is verified against the game build (not added if the game does not support it), then CREATED in every version that does not have it. You can type an ID that already exists in the build here to propagate that component to older versions that are missing it."
              },
              {
                t: "3) Localization Settings (left panel)",
                d: "The component's LANGUAGE-dependent fields. Do NOT type { }, just the content (e.g. \"caption\": \"Counter\"). Written to every active (created) language of each version; existing translations are never overwritten. The 'Current in DB' box below the input shows the component's current value in the DB."
              },
              {
                t: "4) Version Settings (right panel)",
                d: "The component's NON-language fields (position, scale, isEnabled, etc.). Entered the same way without { }; written once per version, not per language."
              },
              {
                t: "5) Update existing values (overwrite)",
                d: "Works in both modes. When checked, existing fields are overwritten; when unchecked, existing fields are skipped and a confirmation popup appears for any conflicts. In 'add component' mode, versions missing the component are created regardless; this checkbox only controls whether versions that ALREADY have the component get their existing values updated."
              },
              {
                t: "6) Apply to DEVELOPMENT / TEST / PRODUCTION",
                d: "Applies the filled panel(s) to the chosen target DB. A dry-run runs first; on a conflict a confirmation popup shows the target DB in capitals. Typical flow: DEVELOPMENT first, then TEST, then PRODUCTION."
              },
              {
                t: "7) Remove fields",
                d: "Shown only in 'update' mode. Removes the fields you entered from every version in the selected DB (via $unset)."
              },
              {
                t: "8) Blank Asset Filler (collapsible, below Apply)",
                d: "Scope: only the components that appear in History (whether ADDED as a new component or UPDATED with new fields). On demand — press 'Check version assets' to scan; it lists the EMPTY asset slots of those components. 'Apply...' fills each empty slot with a placeholder (image→empty.png, atlas→empty.png+json, sound→empty.mp3) and marks it selected. Real assets and components outside History are never touched. Note: components that read FRAMES from inside an asset (atlases/spritesheets, e.g. some jigsaw screens) can break with a blank placeholder — for those use Asset Propagation instead."
              },
              {
                t: "9) Asset Propagation (collapsible, below Apply)",
                d: "Use this when a blank placeholder is not enough and a REAL asset is required. Same scope (History components) and on demand — press 'Check versions' to scan. Then pick a component + the source version you uploaded the real asset to, and 'Apply to missing versions' copies it to every version that doesn't have it (versions that already have it are skipped). Needs at least two versions."
              },
              {
                t: "10) Version Update History (collapsible)",
                d: "Every operation is listed in a user → game tree (default collapsed). Each entry shows the status for the 3 DBs: an applied DB is green '✓ applied by ...' + Remove; a non-applied one shows 'Apply to <ENV>'. If none of the 3 are applied it is flagged yellow 'needs attention'. You move dev→test→prod from here. History is also the source of truth for which components the asset tools (8 & 9) look at."
              },
              {
                t: "11) Pending indicator (top-right ⚠) & preview banner",
                d: "A yellow ⚠ in the top-right of this page means there are updates not yet applied to TEST and PRODUCTION; click it to jump straight to the matching Apply buttons in History. The pop-up banner about pending updates is shown only on the game preview screen, not here."
              },
              {
                t: "12) Guide",
                d: "This help section. The whole page is permission-gated ('updateDatabase' permission) and is now visible in every environment, including production, for users who have that permission."
              }
            ].map((item) => (
              <div key={item.t} style={{ marginTop: "0.75rem" }}>
                <Typography style={{ fontSize: "0.95rem", fontWeight: 600, color: "#9bc0e0" }}>{item.t}</Typography>
                <Typography style={{ fontSize: "0.9rem", color: "#d6d6d6" }}>{item.d}</Typography>
              </div>
            ))}
          </div>
        </Collapse>
      </Card>

      <Dialog
        open={applyConfirmDialog.open}
        onClose={() => setApplyConfirmDialog({ open: false, context: null })}
        maxWidth="xs"
      >
        <DialogTitle disableTypography style={{ textAlign: "center" }}>
          <Typography variant="h6">Confirm Target Database</Typography>
        </DialogTitle>
        <div style={{ padding: "0 24px 8px", textAlign: "center" }}>
          <Typography>This update will be applied to the database:</Typography>
          {applyConfirmDialog.context && renderEnvBanner(applyConfirmDialog.context.env)}
          <Typography>
            <b>Are you sure you want to continue?</b>
          </Typography>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: "16px", padding: "16px" }}>
          <Button
            variant="contained"
            style={{ backgroundColor: "#f44336", color: "#ffffff", fontWeight: "bold" }}
            onClick={() => setApplyConfirmDialog({ open: false, context: null })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#418989", color: "#ffffff", fontWeight: "bold" }}
            onClick={() => {
              const context = applyConfirmDialog.context;
              setApplyConfirmDialog({ open: false, context: null });
              runApply(context);
            }}
          >
            Confirm
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={conflictDialog.open}
        onClose={() => setConflictDialog({ open: false, context: null, conflictCount: 0 })}
        maxWidth="xs"
      >
        <DialogTitle disableTypography style={{ textAlign: "center" }}>
          <Typography variant="h6">Field Already Exists</Typography>
        </DialogTitle>
        <div style={{ padding: "0 24px 8px", textAlign: "center" }}>
          {conflictDialog.context && renderEnvBanner(conflictDialog.context.env)}
          <Typography>
            {`${conflictDialog.conflictCount} field value(s) already exist across the versions. `}
            <b>If you continue, the existing data will be overwritten.</b>
            {" Do you confirm?"}
          </Typography>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: "16px", padding: "16px" }}>
          <Button
            variant="contained"
            style={{ backgroundColor: "#f44336", color: "#ffffff", fontWeight: "bold" }}
            onClick={() => setConflictDialog({ open: false, context: null, conflictCount: 0 })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#418989", color: "#ffffff", fontWeight: "bold" }}
            onClick={() => {
              const context = conflictDialog.context;
              setConflictDialog({ open: false, context: null, conflictCount: 0 });
              applyOperations(context);
            }}
          >
            Overwrite
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={removeConfirmDialog.open}
        onClose={() => setRemoveConfirmDialog({ open: false, entry: null, env: null })}
        maxWidth="xs"
      >
        <DialogTitle disableTypography style={{ textAlign: "center" }}>
          <Typography variant="h6">Remove From Database?</Typography>
        </DialogTitle>
        <div style={{ padding: "0 24px 8px", textAlign: "center" }}>
          <Typography>
            {removeConfirmDialog.entry
              ? `The fields of "${removeConfirmDialog.entry.componentID}" will be removed from every version, on the database:`
              : ""}
          </Typography>
          {removeConfirmDialog.env && renderEnvBanner(removeConfirmDialog.env)}
          <Typography>
            <b>This cannot be undone.</b>
            {" Continue?"}
          </Typography>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: "16px", padding: "16px" }}>
          <Button
            variant="contained"
            style={{ backgroundColor: "#418989", color: "#ffffff", fontWeight: "bold" }}
            onClick={() => setRemoveConfirmDialog({ open: false, entry: null, env: null })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#f44336", color: "#ffffff", fontWeight: "bold" }}
            onClick={() => {
              const { entry, env } = removeConfirmDialog;
              setRemoveConfirmDialog({ open: false, entry: null, env: null });
              removeEntryFromEnv(entry, env);
            }}
          >
            Remove
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={assetWarningDialog.open}
        onClose={() => setAssetWarningDialog({ open: false, counts: null })}
        maxWidth="xs"
      >
        <DialogTitle disableTypography style={{ textAlign: "center" }}>
          <Typography variant="h6">This Component Needs Assets</Typography>
        </DialogTitle>
        <div style={{ padding: "0 24px 8px", textAlign: "center" }}>
          <Typography>
            <b>{componentID.trim()}</b> uses{" "}
            {assetWarningDialog.counts &&
              [
                assetWarningDialog.counts.image ? `${assetWarningDialog.counts.image} image` : null,
                assetWarningDialog.counts.atlas ? `${assetWarningDialog.counts.atlas} atlas` : null,
                assetWarningDialog.counts.sound ? `${assetWarningDialog.counts.sound} sound` : null
              ]
                .filter(Boolean)
                .join(", ")}
            .
          </Typography>
          <Typography style={{ margin: "10px 0" }}>
            Use <b>Blank Asset Filler</b> above to put placeholders in every version now, or go to the game to upload
            the real assets.
          </Typography>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: "16px", padding: "16px" }}>
          <Button
            variant="contained"
            style={{ backgroundColor: "#9aa0a0", color: "#ffffff", fontWeight: "bold" }}
            onClick={() => setAssetWarningDialog({ open: false, counts: null })}
          >
            Stay Here
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#418989", color: "#ffffff", fontWeight: "bold" }}
            onClick={() => {
              setAssetWarningDialog({ open: false, counts: null });
              if (typeof props.onClose === "function") props.onClose();
            }}
          >
            Go to Game
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={emptyFillDialog.open}
        onClose={() => setEmptyFillDialog({ open: false, report: null })}
        maxWidth="xs"
      >
        <DialogTitle disableTypography style={{ textAlign: "center" }}>
          <Typography variant="h6">Fill Empty Asset Slots</Typography>
        </DialogTitle>
        <div style={{ padding: "0 24px 8px", textAlign: "center" }}>
          <Typography>
            {emptyFillDialog.report
              ? `${emptyFillDialog.report.slotWrites} empty slot(s) across ${emptyFillDialog.report.versionsAffected} version(s) will be filled with blank placeholders.`
              : ""}
          </Typography>
          <Typography style={{ margin: "10px 0" }}>
            Existing real assets are <b>not</b> touched. Placeholders are flagged so you can replace them later.
          </Typography>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: "16px", padding: "16px" }}>
          <Button
            variant="contained"
            style={{ backgroundColor: "#9aa0a0", color: "#ffffff", fontWeight: "bold" }}
            onClick={() => setEmptyFillDialog({ open: false, report: null })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#418989", color: "#ffffff", fontWeight: "bold" }}
            onClick={applyEmptyAssets}
          >
            Fill placeholders
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
